import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';

import type { Client, Contact } from '~/api/clients/types';

import { useCachedCreditsafeRecord, useUpdateContact } from '~/api/clients';
import { Blocker, Container, Header, Separator } from '~/components';
import { Form } from '~/components/Form';
import { bypassBlocker } from '~/constants/router';
import { SearchParamKeys } from '~/constants/url';
import { r, routes } from '~/providers/RouterProvider/router.routes';
import { toast } from '~/utils/toast';

import type { ContactFormType } from '../types';

import { ContactAddressFormSection } from '../components/ContactAddressFormSection';
import { ContactCommentsFormSection } from '../components/ContactCommentsFormSection';
import { ContactCustomAgreementFormSection } from '../components/ContactCustomAgreementFormSection';
import { ContactEmailsFormSection } from '../components/ContactEmailsFormSection';
import { formContainerStyle, formSectionsStyle } from '../components/contactFormSections.css';
import { ContactPersonalInfoFormSection } from '../components/ContactPersonalInfoFormSection';
import { OrganisationActions } from '../components/OrganisationActions';
import {
  OrganisationInfoAddress,
  OrganisationInfoCard,
  OrganisationInfoCreditRating,
  OrganisationInfoGeneral,
  OrganisationInfoPeppol,
  OrganisationRefreshVatInformationButton,
} from '../components/OrganisationInfoCard';
import { isClientRefreshable, mapContactFormDataToPayload, mapContactResponseToFormData } from '../utils';

type Props = {
  client: Client;
  contact: Contact;
};

export const EditContactForm = ({ client, contact }: Props) => {
  const isOrganisation = client.clientType === 'organisation';

  const mutation = useUpdateContact(client.id, contact.id);
  const cachedCreditsafeRecord = useCachedCreditsafeRecord(client);
  const creditsafeRecord = client.creditsafeRecord ?? cachedCreditsafeRecord;

  const form = useForm<ContactFormType>({
    defaultValues: mapContactResponseToFormData(contact, client),
  });

  const {
    formState: { isDirty },
    getValues,
    handleSubmit,
  } = form;

  const navigate = useNavigate();
  const searchParams = useSearchParams()[0];
  const redirectPath = searchParams.get(SearchParamKeys.REDIRECT_PATH);
  const { t } = useTranslation(['clients', 'common']);

  const onSubmit = handleSubmit((data) => {
    if (mutation.isPending) return;

    mutation.mutate(mapContactFormDataToPayload(data, getValues('useOrganisationAddress'), client.addresses), {
      onSuccess: () => {
        toast.success({
          description: isOrganisation
            ? t('clients:alerts.successContactEditedOrganisation')
            : t('clients:alerts.successContactEditedPrivate'),
        });

        const redirectRoute = redirectPath ?? (isOrganisation ? r(routes.showClient, { clientId: client.id }) : routes.clients);
        navigate(redirectRoute, { state: bypassBlocker });
      },
      onError: () => toast.danger({ description: t('common:error') }),
    });
  });

  const isBelgianOrganisation = client.addresses.find(({ type }) => type === 'headquarters')?.countryCode === 'BE';

  return (
    <>
      <Header.Root>
        <Header.Content
          backTo={isOrganisation ? r(routes.showClient, { clientId: client.id }) : routes.clients}
          title={isOrganisation ? `${client.name} – ${contact.fullName}` : `${contact.fullName}`}
        />

        <Header.Actions>
          <Header.Action icon="Save" loading={mutation.isPending} onClick={onSubmit}>
            {t('common:actions.save')}
          </Header.Action>
        </Header.Actions>
      </Header.Root>

      <Blocker isBlocked={isDirty} message={t('clients:editContact.blocker')} />

      <Container className={formContainerStyle}>
        {isOrganisation && (
          <OrganisationInfoCard>
            <OrganisationInfoGeneral
              companyName={client.name}
              otherNumber={client.otherNumber}
              refreshVatInformationButton={isClientRefreshable(client) ? <OrganisationRefreshVatInformationButton client={client} /> : null}
              vatInformationLastUpdatedAt={client.vatNumber !== null ? client.vatInformationLastUpdatedAt : null}
              vatNumber={client.vatNumber}
            />
            <OrganisationInfoAddress address={client.addresses.find((address) => address.type === 'headquarters') ?? client.addresses[0]} />
            {isBelgianOrganisation && <OrganisationInfoPeppol peppolId={client.peppolId} />}
            <OrganisationInfoCreditRating
              creditsafeRecord={creditsafeRecord}
              creditsafeStatus={creditsafeRecord !== null ? 'Success' : client.creditsafeStatus}
            />
            <OrganisationActions client={client} />
          </OrganisationInfoCard>
        )}

        <Form autoComplete="off" onSubmit={onSubmit}>
          <div className={formSectionsStyle}>
            <ContactPersonalInfoFormSection form={form} isPrivateClient={!isOrganisation} />

            <Separator />

            <ContactAddressFormSection addresses={client.addresses} form={form} />

            <Separator />

            <ContactEmailsFormSection form={form} peppol={client.peppolId !== null} />

            {client.clientType === 'organisation' && (
              <>
                <Separator />

                <ContactCustomAgreementFormSection form={form} />
              </>
            )}

            <Separator />

            <ContactCommentsFormSection form={form} />
          </div>
        </Form>
      </Container>
    </>
  );
};
